import {
  ReactNode,
  useEffect,
  useRef,
  useState,
  HTMLAttributes,
  isValidElement,
  cloneElement,
} from "react";
import { Helmet } from "react-helmet";
import CssUtils from "../../../utils/css-utils";
import "./page-view.scss";

//<editor-fold desc="Props">
// Omit the children here, as the PageDOMProps is used by the actual page implementations, which do not allow children
export interface PageDOMProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {}
// Instead add the children here, as this is not used elsewhere
interface PageProps
  extends PageDOMProps,
    Pick<HTMLAttributes<HTMLDivElement>, "children"> {
  /**
   * HTML meta data for the page
   */
  meta?: {
    /**
     * HTML>HEAD>TITLE
     */
    title: string;
    /**
     * HTML>HEAD>META[description]
     */
    description: string;
  };
  /**
   * Header for the page content
   */
  header?: ReactNode;
  /**
   * Footer for the page content
   */
  footer?: ReactNode;
}
//</editor-fold>

/**
 * Wraps content
 * @param props
 * @constructor
 */
function Page(props: PageProps) {
  //<editor-fold desc="Local variables">
  let { className, header, footer, meta, children, ...other } = props;
  className = CssUtils.ensureClassNames(className, [
    "page",
    "flex-grow-1",
    "d-flex",
    "flex-column",
  ]);
  // ensure that header h1 has tabIndex so it can be focused after "page load"
  const [headerWithProps, setHeaderWithProps] = useState(
    !!header &&
      isValidElement(header) &&
      header.type === "h1" &&
      header.props.tabIndex === undefined
      ? cloneElement(header, {
          tabIndex: -1,
        } as HTMLAttributes<HTMLHeadingElement>)
      : header
  );
  useEffect(() => {
    if (
      header &&
      isValidElement(header) &&
      header.type === "h1" &&
      header.props.tabIndex === undefined
    ) {
      setHeaderWithProps(
        cloneElement(header, {
          tabIndex: -1,
        } as HTMLAttributes<HTMLHeadingElement>)
      );
    }
  }, [header]);
  // Focus the article or header h1 if any when mounted
  const focusRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const t = focusRef.current?.querySelector("header h1");
    if (t) {
      (t as any).focus(); // why does this need casting???
    } else {
      focusRef.current?.focus();
    }
  }, []);
  //</editor-fold>
  return (
    <>
      {meta && (
        <Helmet>
          {meta.title && <title>{meta.title}</title>}
          {meta.description && (
            <meta name="description" content={meta.description} />
          )}
        </Helmet>
      )}
      <article
        className={className}
        {...other}
        ref={focusRef}
        tabIndex={!header ? -1 : undefined}
      >
        {headerWithProps && (
          <header className={"pt-4 px-4"}>{headerWithProps}</header>
        )}
        <div className="content flex-grow-1 px-4">{children}</div>
        {footer && <footer className="pb-4 px-4">{footer}</footer>}
      </article>
    </>
  );
}

export default Page;
